import React from 'react';
import {
  FormMeta,
  FormState,
  FormErrors,
  DropdownFieldOption
} from '../types/interface';
import TextField from './FormFields/TextField';
import DropdownField from './FormFields/DropdownField';
import ToggleField from './FormFields/ToggleField';
import CheckboxField from './FormFields/CheckBoxField';
import { isConditionMet } from '../utils/MdmUtils';
import { isFieldRequired } from '../utils/validationUtils';

interface GenericFormProps {
  intl: any;
  fields: FormMeta[];
  formValues: FormState;
  formErrors: FormErrors;
  onChange: (fieldName: string, value: string | number | boolean) => void;
  disabled?: boolean;
  showDeviceTypeBadges?: boolean;
}

const GenericForm: React.FC<GenericFormProps> = ({
  intl,
  fields,
  formValues,
  formErrors,
  onChange,
  disabled = false,
  showDeviceTypeBadges = false
}) => {
  const renderField = (field: FormMeta) => {
    // Check if field should be visible based on condition
    if (field.condition && !isConditionMet(field.condition, formValues)) {
      return null;
    }

    const value = formValues[field.name];
    const error = formErrors[field.name];
    const fieldDisabled = disabled;

    const fieldRequired = isFieldRequired(field, formValues);

    const commonProps = {
      intl,
      label: field.label,
      disabled: fieldDisabled,
      showDeviceTypeBadges,
      level: 0,
      fieldId: field.id, // Pass field.id for unique identification
      uiDataProp: {
        vertical: true,
        label: field.label,
        requiredIndicator: fieldRequired
      }
    };

    switch (field.type) {
      case 'text':
      case 'password':
      case 'number':
        return (
          <TextField
            key={field.id}
            {...commonProps}
            value={String(value || '')}
            onChange={(newValue: string) => {
              const convertedValue =
                field.type === 'number' ? Number(newValue) || 0 : newValue;
              onChange(field.name, convertedValue);
            }}
            placeholder={field.placeholder}
            type={field.type}
            error={error}
            minValue={
              field.type === 'number' ? field.validation?.min : undefined
            }
            maxValue={
              field.type === 'number' ? field.validation?.max : undefined
            }
            supportedDeviceTypes={[]}
          />
        );

      case 'select':
        return (
          <DropdownField
            key={field.id}
            {...commonProps}
            value={String(value || field.defaultValue || '')}
            onChange={(newValue: string) => onChange(field.name, newValue)}
            options={(field.options as DropdownFieldOption[]) || []}
            supportedDeviceTypes={[]}
          />
        );

      case 'toggle':
        return (
          <ToggleField
            key={field.id}
            {...commonProps}
            checked={Boolean(value)}
            onChange={(newValue: boolean) => onChange(field.name, newValue)}
            supportedDeviceTypes={[]}
            isChildAttribute={false}
          />
        );
      case 'checkbox':
        return (
          <CheckboxField
            key={field.id}
            {...commonProps}
            checked={Boolean(value)}
            onChange={(newValue: boolean) => onChange(field.name, newValue)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex col g10">
      {fields.map((field) => renderField(field))}
    </div>
  );
};

export default GenericForm;
